import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ModuleList } from "@/components/lms/ModuleList";
import { ModuleContent } from "@/components/lms/ModuleContent";
import { ModuleLockedPanel } from "@/components/lms/ModuleLockedPanel";
import { ProgressBar } from "@/components/lms/ProgressBar";
import { DiscussionList } from "@/components/lms/DiscussionList";
import { HomeworkSubmissionSection } from "@/components/lms/HomeworkSubmissionSection";
import { QABoardSection } from "@/components/lms/QABoardSection";
import { HelpRequestsSection } from "@/components/lms/HelpRequestsSection";
import { AssignmentsSection } from "@/components/lms/AssignmentsSection";
import { QuizSection } from "@/components/lms/QuizSection";
import { SurveyDialog } from "@/components/lms/SurveyDialog";
import { ModuleCompleteDialog } from "@/components/lms/ModuleCompleteDialog";
import { CurriculumCompleteDialog } from "@/components/lms/CurriculumCompleteDialog";
import { useCheckAndIssueCertificate, type Certificate } from "@/hooks/useCertificates";
import { useProfile } from "@/hooks/useProfile";
import { useModuleRequirements, useCanProgressFromModule } from "@/hooks/useModuleRequirements";
import { ModuleLockCheck } from "@/components/course/ModuleLockCheck";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { useCourseCurriculum } from "@/hooks/useCourseCurriculum";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useCourse,
  useModules,
  useModuleContent,
  useEnrollCourse,
  useCompleteModule,
  useUnenrollCourse,
} from "@/hooks/useCourses";
import { useGatedCohortCheck } from "@/hooks/useCohorts";
import { useCohortModuleGating, useIsModuleCohortLocked } from "@/hooks/useCohortModuleGating";
import { usePendingSurvey } from "@/hooks/useCourseSurveys";
import { useHasRole } from "@/hooks/useUserRoles";
import { useAssignCourse, useCourseAssignments } from "@/hooks/useAssignmentsAdmin";
import { AssignUserDialog } from "@/components/admin/AssignUserDialog";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  MessageSquare,
  Play,
  FileText,
  HelpCircle,
  Hand,
  ClipboardList,
  LogOut,
  UserPlus,
  AlertCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function CourseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("content");
  const [showSurvey, setShowSurvey] = useState(false);
  const [lastShownMilestone, setLastShownMilestone] = useState<number | null>(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completedModuleName, setCompletedModuleName] = useState("");
  const [showCurriculumCompleteDialog, setShowCurriculumCompleteDialog] = useState(false);
  const [earnedCertificate, setEarnedCertificate] = useState<Certificate | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [urlParamsHandled, setUrlParamsHandled] = useState(false);

  // Read URL query params for deep linking to specific module/assignment
  const urlModuleId = searchParams.get("module");
  const urlAssignmentId = searchParams.get("assignment");
  const urlAction = searchParams.get("action"); // 'resubmit' or 'view'

  const isAdmin = useHasRole("admin");
  const assignCourse = useAssignCourse();
  const { data: assignments } = useCourseAssignments(id || "");
  const assignedUserIds = assignments?.map((a) => a.user_id) || [];

  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: course, isLoading: courseLoading } = useCourse(id!);
  const checkAndIssueCertificate = useCheckAndIssueCertificate();
  const { data: modules, isLoading: modulesLoading } = useModules(id!);
  const { data: content, isLoading: contentLoading } = useModuleContent(
    selectedModuleId || ""
  );
  const { data: moduleRequirements, isLoading: requirementsLoading } = useModuleRequirements(
    selectedModuleId || undefined
  );
  const { data: progressionCheck } = useCanProgressFromModule(selectedModuleId || undefined);
  const instructorAvatarUrl = useStorageUrl("avatars", course?.instructor?.avatar_url);
  const { data: courseCurriculum } = useCourseCurriculum(id);

  const enrollMutation = useEnrollCourse();
  const completeMutation = useCompleteModule();
  const unenrollMutation = useUnenrollCourse();
  const { data: gatedCohortEnrollment } = useGatedCohortCheck(id!);
  const { data: cohortLockedMap } = useCohortModuleGating(id!);
  const { isLocked: isSelectedModuleCohortLocked } = useIsModuleCohortLocked(id!, selectedModuleId);
  
  const pendingSurvey = usePendingSurvey(id!, course?.progress || 0, selectedModuleId);
  
  // Show survey dialog when a new milestone/module survey is available
  useEffect(() => {
    if (pendingSurvey) {
      const surveyKey = pendingSurvey.milestone || pendingSurvey.module_id;
      if (surveyKey !== lastShownMilestone) {
        setShowSurvey(true);
        setLastShownMilestone(surveyKey as number);
      }
    }
  }, [pendingSurvey, lastShownMilestone]);

  // Handle URL query parameters for deep linking (module, assignment, action)
  useEffect(() => {
    if (modules && modules.length > 0 && !urlParamsHandled) {
      // If URL has module param, select that module and switch to assignments tab
      if (urlModuleId) {
        const targetModule = modules.find((m) => m.id === urlModuleId);
        // Must respect is_unlocked: this used to accept ANY module id, so
        // /courses/<id>?module=<lockedId> opened locked content directly.
        if (targetModule && !targetModule.is_unlocked) {
          toast({
            title: "Module locked",
            description: "Complete the earlier modules first.",
            variant: "destructive",
          });
        }
        if (targetModule && targetModule.is_unlocked) {
          setSelectedModuleId(urlModuleId);
          // If there's an assignment param, switch to assignments tab
          if (urlAssignmentId) {
            setActiveTab("assignments");
          }
          setUrlParamsHandled(true);
          return;
        }
      }
      
      // Default: auto-select first unlocked module
      if (!selectedModuleId) {
        const firstUnlocked = modules.find((m) => m.is_unlocked);
        if (firstUnlocked) {
          setSelectedModuleId(firstUnlocked.id);
        }
      }
      setUrlParamsHandled(true);
    }
  }, [modules, urlModuleId, urlAssignmentId, urlParamsHandled, selectedModuleId, toast]);

  const selectedModule = modules?.find((m) => m.id === selectedModuleId);
  const currentIndex = modules?.findIndex((m) => m.id === selectedModuleId) ?? -1;
  const nextModule = currentIndex >= 0 ? modules?.[currentIndex + 1] : null;
  const prevModule = currentIndex > 0 ? modules?.[currentIndex - 1] : null;

  const handleEnroll = async () => {
    try {
      await enrollMutation.mutateAsync(id!);
      toast({
        title: "Enrolled!",
        description: "You've successfully enrolled in this course.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to enroll in course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleComplete = async () => {
    if (!selectedModuleId || !id) return;

    try {
      await completeMutation.mutateAsync({
        moduleId: selectedModuleId,
        courseId: id,
      });
      
      // Show congratulations dialog instead of toast
      setCompletedModuleName(selectedModule?.title || "this module");
      setShowCompleteDialog(true);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to mark module as complete. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleContinueAfterComplete = useCallback(async () => {
    setShowCompleteDialog(false);
    
    // Check if any curriculum containing this course is now complete
    if (user && profile && course) {
      // Get curricula that contain this course
      const { data: curriculumCourses } = await supabase
        .from("curriculum_courses")
        .select("curriculum_id, curricula(id, title)")
        .eq("course_id", id!);
      
      if (curriculumCourses && curriculumCourses.length > 0) {
        for (const cc of curriculumCourses) {
          const curriculum = cc.curricula as any;
          if (curriculum) {
            try {
              const certificate = await checkAndIssueCertificate.mutateAsync({
                curriculumId: curriculum.id,
                curriculumTitle: curriculum.title,
                userName: profile.full_name,
              });
              
              if (certificate) {
                setEarnedCertificate(certificate);
                setShowCurriculumCompleteDialog(true);
                return; // Show curriculum complete dialog instead of navigating
              }
            } catch (error) {
              console.error("Error checking curriculum completion:", error);
            }
          }
        }
      }
    }
    
    // Navigate to next module if available
    if (nextModule && nextModule.is_unlocked) {
      setSelectedModuleId(nextModule.id);
    }
  }, [user, profile, course, id, checkAndIssueCertificate, nextModule]);

  const handleUnenroll = async () => {
    try {
      await unenrollMutation.mutateAsync(id!);
      toast({
        title: "Unenrolled",
        description: "You've successfully unenrolled from this course.",
      });
      navigate("/courses");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to unenroll from course. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (courseLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Course not found</h2>
          <Button
            variant="link"
            onClick={() => navigate(courseCurriculum ? `/curricula/${courseCurriculum.curriculumId}` : "/courses")}
            className="mt-4"
          >
            {courseCurriculum ? `Back to ${courseCurriculum.curriculumTitle}` : "Back to courses"}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(courseCurriculum ? `/curricula/${courseCurriculum.curriculumId}` : "/courses")}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> {courseCurriculum ? `Back to ${courseCurriculum.curriculumTitle}` : "Back to courses"}
            </Button>
            <h1 className="text-2xl font-bold">{course.title}</h1>
            {course.description && (
              <p className="text-muted-foreground mt-1">{course.description}</p>
            )}

            {course.instructor && (
              <div className="flex items-center gap-2 mt-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={instructorAvatarUrl || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {course.instructor.full_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm">
                  Instructor: <strong>{course.instructor.full_name}</strong>
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setShowAssignDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Users
              </Button>
            )}
            {!course.is_enrolled ? (
              gatedCohortEnrollment ? (
                <Badge variant="secondary" className="py-2 px-3">
                  You are enrolled in this curriculum through a cohort program.
                </Badge>
              ) : (
                <Button
                  onClick={handleEnroll}
                  disabled={enrollMutation.isPending}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {enrollMutation.isPending ? "Enrolling..." : "Enroll Now"}
                </Button>
              )
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <LogOut className="h-4 w-4 mr-2" />
                    Unenroll
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unenroll from course?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3">
                        <p>
                          Are you sure you want to unenroll from "{course.title}"?
                        </p>
                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                          <p className="font-medium text-destructive mb-2">The following will be permanently reset:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            <li>Module progress and completion status</li>
                            <li>Quiz attempts and scores</li>
                            <li>Assignment submissions</li>
                            <li>Homework submissions</li>
                          </ul>
                        </div>
                        <p className="text-sm">
                          You'll need to re-enroll and start from the beginning to access this course again.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleUnenroll}
                      disabled={unenrollMutation.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {unenrollMutation.isPending ? "Unenrolling..." : "Proceed"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Progress bar for enrolled users */}
        {course.is_enrolled && (
          <ProgressBar progress={course.progress || 0} size="lg" />
        )}

        {/* Main content area */}
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          {/* Module sidebar */}
          <Card className="h-fit lg:sticky lg:top-24">
            <CardContent className="py-4">
              {modulesLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : modules && modules.length > 0 ? (
                <ModuleList
                  modules={modules}
                  currentModuleId={selectedModuleId || undefined}
                  onSelectModule={setSelectedModuleId}
                  isEnrolled={course.is_enrolled || false}
                  cohortLockedMap={cohortLockedMap}
                />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No modules available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Content area */}
          <div className="space-y-6">
            {!course.is_enrolled ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    {gatedCohortEnrollment
                      ? "You are enrolled in this curriculum through a cohort program."
                      : "Enroll to access course content"}
                  </h3>
                  {!gatedCohortEnrollment && (
                    <>
                      <p className="text-muted-foreground mb-4">
                        Start your learning journey by enrolling in this course
                      </p>
                      <Button onClick={handleEnroll} disabled={enrollMutation.isPending}>
                        {enrollMutation.isPending ? "Enrolling..." : "Enroll Now"}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="content">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Content
                  </TabsTrigger>
                  <TabsTrigger value="assignments">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Assignments
                  </TabsTrigger>
                  <TabsTrigger value="homework">
                    <FileText className="h-4 w-4 mr-2" />
                    Homework
                  </TabsTrigger>
                  <TabsTrigger value="qa">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Q&A
                  </TabsTrigger>
                  <TabsTrigger value="discussions">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Discussions
                  </TabsTrigger>
                  <TabsTrigger value="help">
                    <Hand className="h-4 w-4 mr-2" />
                    Help
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="space-y-6">
                  {selectedModule ? (
                    <>
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            {selectedModule.is_completed && (
                              <CheckCircle2 className="h-5 w-5 text-success" />
                            )}
                            {selectedModule.title}
                          </CardTitle>
                          {selectedModule.description && (
                            <p className="text-muted-foreground">
                              {selectedModule.description}
                            </p>
                          )}
                        </CardHeader>
                      </Card>

                      {/* Checked BOTH gates: cohort lock AND progression unlock.
                          This previously checked only the cohort lock, so a
                          locked module's content rendered if it was selected. */}
                      {isSelectedModuleCohortLocked || selectedModule.is_unlocked === false ? (
                        <ModuleLockedPanel />
                      ) : contentLoading ? (
                        <Skeleton className="h-64 w-full" />
                      ) : (
                        <>
                          <ModuleContent content={content || []} />
                          <QuizSection moduleId={selectedModuleId!} />
                        </>
                      )}

                      {/* Navigation buttons */}
                      {!isSelectedModuleCohortLocked && (
                      <div className="flex items-center justify-between pt-4">
                        <Button
                          variant="outline"
                          onClick={() => prevModule && setSelectedModuleId(prevModule.id)}
                          disabled={!prevModule}
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Previous
                        </Button>

                        <div className="flex flex-col gap-3">
                          {/* Show detailed lock check for required assignments not passed */}
                          {!selectedModule.is_completed && 
                           progressionCheck && 
                           !progressionCheck.canProgress && 
                           progressionCheck.missingAssignments.length > 0 && (
                            <ModuleLockCheck
                              reason={progressionCheck.reason || "Complete required assignments to proceed."}
                              missingAssignments={progressionCheck.missingAssignments}
                              courseId={id!}
                              moduleId={selectedModuleId || undefined}
                              onViewAssignments={() => setActiveTab("assignments")}
                            />
                          )}
                          
                          {/* Show general requirements (quizzes, optional assignments) if no required assignment issues */}
                          {!selectedModule.is_completed && 
                           moduleRequirements && 
                           !moduleRequirements.isComplete && 
                           (progressionCheck?.canProgress || progressionCheck?.missingAssignments.length === 0) && (
                            <Alert variant="default" className="border-warning/50 bg-warning/10">
                              <AlertCircle className="h-4 w-4 text-warning" />
                              <AlertDescription className="text-sm">
                                <span className="font-medium">Complete the following to proceed:</span>
                                <ul className="mt-1 list-disc list-inside text-muted-foreground">
                                  {moduleRequirements.missingRequirements.map((req, idx) => (
                                    <li key={idx}>{req}</li>
                                  ))}
                                </ul>
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          <div className="flex gap-2">
                            {!selectedModule.is_completed && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        onClick={handleComplete}
                                        disabled={
                                          completeMutation.isPending || 
                                          requirementsLoading || 
                                          !moduleRequirements?.isComplete
                                        }
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        {completeMutation.isPending
                                          ? "Completing..."
                                          : "Mark Complete"}
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!moduleRequirements?.isComplete && (
                                    <TooltipContent side="top">
                                      <p>Complete all quizzes and assignments first</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}

                            {nextModule && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        variant="outline"
                                        onClick={() => setSelectedModuleId(nextModule.id)}
                                        disabled={
                                          !nextModule.is_unlocked || 
                                          (!selectedModule.is_completed && !moduleRequirements?.isComplete)
                                        }
                                      >
                                        Next
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {(!nextModule.is_unlocked || (!selectedModule.is_completed && !moduleRequirements?.isComplete)) && (
                                    <TooltipContent side="top">
                                      <p>Complete current module requirements first</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      </div>
                      )}
                    </>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">
                          Select a module to begin learning
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="assignments">
                  {selectedModuleId ? (
                    <AssignmentsSection 
                      moduleId={selectedModuleId} 
                      onSubmitSuccess={() => setActiveTab("content")} 
                      selectedAssignmentId={urlAssignmentId}
                    />
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          Select a module to view assignments
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="homework">
                  {selectedModuleId ? (
                    <HomeworkSubmissionSection
                      moduleId={selectedModuleId}
                      courseInstructorId={course.instructor?.id}
                    />
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          Select a module to view homework assignments
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="qa">
                  <QABoardSection
                    courseId={id!}
                    courseInstructorId={course.instructor?.id}
                  />
                </TabsContent>

                <TabsContent value="discussions">
                  <DiscussionList 
                    courseId={id!} 
                    moduleId={selectedModuleId}
                  />
                </TabsContent>

                <TabsContent value="help">
                  <HelpRequestsSection
                    courseId={id!}
                    moduleId={selectedModuleId}
                    moduleName={selectedModule?.title}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
        
        {/* Survey Dialog */}
        <SurveyDialog
          survey={pendingSurvey}
          open={showSurvey}
          onClose={() => {
            setShowSurvey(false);
            navigate('/dashboard');
          }}
        />

        {/* Module Complete Congratulations Dialog */}
        <ModuleCompleteDialog
          open={showCompleteDialog}
          onOpenChange={setShowCompleteDialog}
          moduleName={completedModuleName}
          hasNextModule={!!nextModule && nextModule.is_unlocked}
          isLastModule={!nextModule}
          onContinue={handleContinueAfterComplete}
        />

        {/* Curriculum Complete Dialog */}
        <CurriculumCompleteDialog
          open={showCurriculumCompleteDialog}
          onOpenChange={setShowCurriculumCompleteDialog}
          certificate={earnedCertificate}
        />

        {/* Assign Users Dialog */}
        {isAdmin && (
          <AssignUserDialog
            open={showAssignDialog}
            onOpenChange={setShowAssignDialog}
            type="course"
            itemId={id || ""}
            itemTitle={course?.title || ""}
            onAssign={async (userId) => {
              await assignCourse.mutateAsync({
                courseId: id!,
                userId,
              });
            }}
            assignedUserIds={assignedUserIds}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
